import { Box } from "@mui/material";
import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { IProfileReport } from "../../../../../models/profile-report";
import { ProfileReportsService } from "../../../../../services/profileReports.service";
import { ProfileReport } from "./ProfileReportForm/ProfileReport";

const translationPath = "page.register.roles.";

const ProfileReportsPage = () => {
  const { id: roleId } = useParams();
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [rows, setRows] = useState<IProfileReport[]>([]);

  const profileReportsService = useMemo(() => new ProfileReportsService(), []);

  const columns: GridColDef<IProfileReport>[] = [
    {
      field: "reportCode",
      headerName: t(translationPath + "code") || "Code",
      width: 100,
      sortable: false,
      renderCell: (params) => params.row.report?.code ?? "-",
    },
    {
      field: "report",
      headerName: t(translationPath + "report") || "Report",
      flex: 1,
      renderCell: (params) =>
        params.value ? (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        ),
    },
  ];

  const handleAdd = () => {
    setSelectedId(undefined);
    setShowForm(true);
  };

  const handleRowClick = (params: GridRowParams<IProfileReport>) => {
    if (!params.row) return;
    setSelectedId(params.row.id);
    setShowForm(true);
  };

  const handleBack = () => {
    setSelectedId(undefined);
    setShowForm(false);
  };

  const params = useMemo(
    () => ({
      RoleId: roleId,
    }),
    [roleId],
  );

  if (!roleId) return null;

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      {showForm ? (
        <Box
          sx={(theme) => ({
            p: 1,
            height: "100%",
            overflow: "auto",
            backgroundColor: theme.palette.background.default,
            borderRadius: 1,
          })}
        >
          <ProfileReport
            id={selectedId}
            roleId={roleId}
            rows={rows}
            onBack={handleBack}
          />
        </Box>
      ) : (
        <Box height={600}>
          <Table<IProfileReport>
            showDefaultMacroFilters={false}
            service={profileReportsService}
            serviceProps={params}
            columns={columns}
            title={t(translationPath + "reports") || "Reports"}
            searchInput
            onAdd={handleAdd}
            onRowClick={handleRowClick}
            onFetchData={(data) => {
              setRows(data);
            }}
            defaultPageSize={20}
            pageSizeOptions={[5, 10, 20]}
            confirmOptions={{
              title:
                t(translationPath + "linked_report_remove_title") ||
                "Confirm Deletion",
              description: t(
                translationPath + "linked_report_remove_description",
              ),
              confirmationButtonProps: {
                variant: "contained",
                color: "error",
              },
            }}
            searchInputPlaceHolder={
              t(translationPath + "search_report") || "Search Report..."
            }
            addButtonLabel={t(translationPath + "add_report") || "Add Report"}
          />
        </Box>
      )}
    </Box>
  );
};

export default memo(ProfileReportsPage);
